import type { ReactNode } from 'react'
import styles from './DataTable.module.css'

export interface Column<Row> {
    key: string
    header: string
    /** Right-align and tabular-align numeric columns. */
    numeric?: boolean
    /** Machine output -- addresses, AS numbers, endpoints -- set in monospace. */
    mono?: boolean
    render: (row: Row) => ReactNode
}

export type SortDirection = 'asc' | 'desc'

interface DataTableProps<Row> {
    columns: Column<Row>[]
    rows: Row[]
    rowKey: (row: Row, index: number) => string
    /** Visually hidden unless the table has no surrounding Panel title. */
    caption: string
    captionVisible?: boolean
    sortKey?: string
    sortDirection?: SortDirection
    onSort?: (key: string) => void
    empty?: ReactNode
}

/**
 * Real <thead>/<th scope="col"> with aria-sort. The old results table used
 * <td class="text-bold"> for its header row, which is invisible to assistive
 * technology as a header.
 */
export default function DataTable<Row>({
    columns, rows, rowKey, caption, captionVisible,
    sortKey, sortDirection, onSort, empty,
}: DataTableProps<Row>) {
    if (rows.length === 0 && empty) return <div className={styles.empty}>{empty}</div>

    return (
        <div className={styles.scroll}>
            <table className={styles.table}>
                <caption className={captionVisible ? styles.caption : 'visually-hidden'}>{caption}</caption>
                <thead>
                    <tr>
                        {columns.map((col) => {
                            const active = sortKey === col.key
                            const ariaSort = active
                                ? sortDirection === 'asc' ? 'ascending' : 'descending'
                                : undefined
                            return (
                                <th
                                    key={col.key}
                                    scope="col"
                                    aria-sort={ariaSort}
                                    className={[
                                        col.numeric ? styles.thNumeric : styles.th,
                                        active ? styles.thSorted : '',
                                    ].filter(Boolean).join(' ')}
                                >
                                    {onSort ? (
                                        <button
                                            type="button"
                                            className={styles.sort}
                                            onClick={() => onSort(col.key)}
                                        >
                                            {col.header}
                                            <span aria-hidden="true" className={styles.caret}>
                                                {active ? (sortDirection === 'asc' ? '▲' : '▼') : '　'}
                                            </span>
                                        </button>
                                    ) : col.header}
                                </th>
                            )
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={rowKey(row, i)} className={styles.row}>
                            {columns.map((col) => (
                                <td key={col.key} className={[
                                    col.numeric ? styles.tdNumeric : styles.td,
                                    col.mono ? styles.mono : '',
                                ].filter(Boolean).join(' ')}>
                                    {col.render(row)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
